// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Grid2, IconButton, Stack, Typography } from '@mui/material';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { TFunction } from 'i18next';
// Components, Layouts, Pages
import { BaseDatePicker, BaseLabel, BaseStatus, BaseTable, Loading, UnderDevelopment } from '~/components/common';
// Others
import { IMarketingRevenueReport, IQueryParamListExport } from '~/utils/interface/reports';
import { BACK_TO_PREVIOUS_PAGE, DEFAULT_CURRENT_PAGE, DEFAULT_LIMIT_PAGE, EMPTY_VALUE } from '~/utils/constants/common';
import { IPaginationResponse } from '~/utils/interface/base';
import { IHeaderCellType } from '~/utils/interface/common';
import { formatCurrencyUSD, getFullName } from '~/utils/helper/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { exportDataReport, getListMarketingRevenueReport } from '~/thunks/reports/reportsThunk';
import { ReportTypeEnum } from '~/utils/enum/common';
// Styles, images, icons
import styles from './ReportsMarketingRevenue.module.scss';
import { icons } from '~/assets';

const cx = classNames.bind(styles);

const columns = (t: TFunction): IHeaderCellType<IMarketingRevenueReport>[] => {
  return [
    {
      id: 'firstName',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_client_name')}</Box>
        </Stack>
      ),
      render: (record: IMarketingRevenueReport) => {
        return getFullName({
          firstName: record?.firstName,
          lastName: record?.lastName,
        });
      },
    },
    {
      id: 'companyName',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_company')}</Box>
        </Stack>
      ),
      render: (record: IMarketingRevenueReport) => {
        return record?.companyName || EMPTY_VALUE;
      },
    },
    {
      id: 'type',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_type')}</Box>
        </Stack>
      ),
      render: (record: IMarketingRevenueReport) => (record?.type ? <BaseStatus label={record?.type} /> : EMPTY_VALUE),
    },
    {
      id: 'status',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_status')}</Box>
        </Stack>
      ),
      render: (record: IMarketingRevenueReport) =>
        record?.status ? <BaseStatus label={record?.status} /> : EMPTY_VALUE,
    },
    {
      id: 'orderQuantity',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_order_qty')}</Box>
        </Stack>
      ),
      render: (record: IMarketingRevenueReport) => {
        return <Typography className={cx('textQty')}>{record?.orderQuantity || EMPTY_VALUE}</Typography>;
      },
    },
    {
      id: 'total',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_total')}</Box>
        </Stack>
      ),
      render: (record: IMarketingRevenueReport) => {
        return (
          <Typography className={cx('textQty')}>
            {formatCurrencyUSD({ value: record?.total, showDecimal: true })}
          </Typography>
        );
      },
    },
    {
      id: 'totalWithoutTax',
      label: (
        <Stack direction={'row'} alignItems={'center'} gap={2}>
          <Box>{t('report_table_total_without_tax')}</Box>
        </Stack>
      ),
      render: (record: IMarketingRevenueReport) => {
        return (
          <Typography className={cx('textQty')}>
            {formatCurrencyUSD({ value: record?.totalWithoutTax, showDecimal: true })}
          </Typography>
        );
      },
    },
  ];
};

const ReportsMarketingRevenue = () => {
  //#region Destructuring Props
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/reports', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
  const pageSelected = useMemo<number>(() => Number(params?.page ?? DEFAULT_CURRENT_PAGE), [params?.page]);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [marketingRevenueList, setMarketingRevenueList] = useState<IMarketingRevenueReport[]>([]);
  const [pagination, setPagination] = useState<IPaginationResponse>();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paramsObject, setParamsObject] = useState<IQueryParamListExport>({
    page: DEFAULT_CURRENT_PAGE,
    limit: DEFAULT_LIMIT_PAGE,
  });
  const [isShowUnderDevelopment, setIsShowUnderDevelopment] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetListMarketingRevenue();
  }, [paramsObject.page]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetListMarketingRevenue = () => {
    setIsLoading(true);

    dispatch(getListMarketingRevenueReport(paramsObject))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        const { responses, pagination } = res.data;

        setMarketingRevenueList(responses);
        setPagination(pagination);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleApplyFilter = () => {
    handleGetListMarketingRevenue();
  };

  const handleExport = () => {
    setIsLoading(true);

    const params: IQueryParamListExport = {
      ...paramsObject,
      type: ReportTypeEnum.MARKETING_REVENUE,
    };

    dispatch(exportDataReport(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        handleDownloadFile(data?.url);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  };

  const handleDownloadFile = (fileUrl: string) => {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = fileUrl;
    link.click();
  };

  const handleShowUnderDevelopment = () => {
    setIsShowUnderDevelopment(!isShowUnderDevelopment);
  };

  const handlePageChange = (_event: ChangeEvent<unknown>, page: number) => {
    setParamsObject({ ...paramsObject, page });
    setSearchParams({ ...params, page: String(page) });
  };

  const handleBack = () => {
    navigate(BACK_TO_PREVIOUS_PAGE);
  };
  //#endregion Handle Function

  return (
    <Stack className={cx('container')} spacing={6}>
      <Stack direction='row' spacing={2} alignItems='center'>
        <IconButton color='primary' onClick={handleBack} className={cx('buttonBack')}>
          <img src={icons.commonIconBack} alt={t('common_alt_img', { ns: 'common/base' })} />
        </IconButton>

        <Stack direction={'column'} spacing={2}>
          <BaseLabel label={t('reports_marketing_revenue_title')} styleType='bigTitle' />
          <Typography className={cx('description')}>{t('reports_marketing_revenue_desc_page')}</Typography>
        </Stack>
      </Stack>

      <Stack direction={'column'} gap={5} className={cx('viewAction')}>
        <Grid2 columnSpacing={'24px'} alignItems={'start'} rowSpacing={'24px'} container>
          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_start_date', { ns: 'common/base' })}
              value={paramsObject.startDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  startDate: date as string,
                }));
              }}
            />
          </Grid2>

          <Grid2 size={3}>
            <BaseDatePicker
              mode='date'
              height={40}
              label={t('common_text_end_date', { ns: 'common/base' })}
              value={paramsObject.endDate}
              onChange={(date) => {
                setParamsObject((prevState) => ({
                  ...prevState,
                  endDate: date as string,
                }));
              }}
            />
          </Grid2>
        </Grid2>

        <Stack direction='row' justifyContent={'space-between'} alignItems='center'>
          <Button
            type='button'
            variant='contained'
            className={cx('buttonApply')}
            onClick={handleApplyFilter}
            startIcon={<img src={icons.commonIconFilter} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_action_apply', { ns: 'common/base' })}
          </Button>

          <Button
            type='button'
            variant='contained'
            className={cx('buttonExport')}
            onClick={handleExport}
            disabled={!marketingRevenueList.length}
            startIcon={<img src={icons.commonIconExportExcel} alt={t('common_alt_img', { ns: 'common/base' })} />}
          >
            {t('common_button_export_excel', { ns: 'common/base' })}
          </Button>
        </Stack>
      </Stack>

      <Box className={cx('tableContainer')}>
        <BaseTable
          dataSource={marketingRevenueList}
          currentPage={pageSelected}
          totalPage={pagination?.totalPages}
          columns={columns(t)}
          onPageChange={handlePageChange}
        />
      </Box>

      {isShowUnderDevelopment && <UnderDevelopment onClose={handleShowUnderDevelopment} />}

      {isLoading && <Loading />}
    </Stack>
  );
};

export default ReportsMarketingRevenue;
